import { useEffect, useRef, useState } from "react";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { useAuth } from "@/context/AuthContext";
import type { Language } from "@/utils/dictionary";
import { Step1SelectProduct } from "@/feature/ads/cretives/components/modals/steps/Step1SelectProduct";
import { Step2VideoStudio } from "./Step2VideoStudio";

type Step = 1 | 2;

interface CreateVideoModalProps {
  open: boolean;
  onClose: () => void;
  initialProduct?: ProductInfoCreative | null;
}

export function CreateVideoModal({ open, onClose, initialProduct }: CreateVideoModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { language } = useAuth();
  const [step, setStep] = useState<Step>(initialProduct ? 2 : 1);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfoCreative | null>(initialProduct ?? null);

  useEffect(() => {
    if (!open) return;
    if (initialProduct) {
      setStep(2);
      setSelectedProduct(initialProduct);
    } else {
      setStep(1);
      setSelectedProduct(null);
    }
  }, [open, initialProduct]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!modalRef.current?.contains(target)) {
        const isInsideNestedModal =
          target instanceof Element && target.closest("[data-fluxi-nested-modal]");
        if (!isInsideNestedModal) {
          onClose();
        }
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        ref={modalRef}
        className={`relative bg-background rounded-2xl shadow-2xl overflow-hidden border border-border/50 ${
          step === 2
            ? "w-full max-w-5xl h-[90vh] max-h-[860px]"
            : "w-full max-w-md"
        }`}
      >
        {step === 1 && (
          <Step1SelectProduct
            language={language as Language}
            onClose={onClose}
            onSelectProduct={(product) => {
              setSelectedProduct(product);
              setStep(2);
            }}
          />
        )}
        {step === 2 && selectedProduct && (
          <Step2VideoStudio
            product={selectedProduct}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}
